import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  QrCode, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Camera,
  CameraOff,
  Users,
  Ticket,
  RefreshCw,
  Search,
  ArrowLeft,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TicketScanner() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { isAuthenticated, isInfluencer, loading: authLoading } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(eventId || '');
  const [checkInStats, setCheckInStats] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [startingCamera, setStartingCamera] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchEvents();
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (selectedEvent) {
      fetchCheckInStats();
    }
  }, [selectedEvent]);

  useEffect(() => {
    return () => {
      // Cleanup scanner on unmount
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(console.error);
      }
    };
  }, []);

  const fetchEvents = async () => {
    try {
      // Try to get creator's events first
      const res = await axios.get(`${API_URL}/influencer/events`);
      const publishedEvents = res.data.filter(e => e.status === 'published');
      if (publishedEvents.length > 0) {
        setEvents(publishedEvents);
        if (!selectedEvent) {
          setSelectedEvent(publishedEvents[0].id);
        }
        return;
      }
    } catch (error) {
      // Not a creator, continue to check team membership
    }
    
    // If not a creator or no events, try to get team events
    try {
      const teamRes = await axios.get(`${API_URL}/team/my-teams`);
      if (teamRes.data.length > 0) {
        // Collect all assigned events from team memberships
        let accessibleEvents = [];
        
        for (const membership of teamRes.data) {
          if (membership.assigned_events && membership.assigned_events.length > 0) {
            // Add events with creator info
            const eventsWithCreator = membership.assigned_events.map(e => ({
              ...e,
              creator_name: membership.creator?.display_name,
              influencer_id: membership.influencer_id
            }));
            accessibleEvents = [...accessibleEvents, ...eventsWithCreator];
          }
        }
        
        // Remove duplicates
        const uniqueEvents = accessibleEvents.filter((e, i, arr) => 
          arr.findIndex(x => x.id === e.id) === i
        );
        
        setEvents(uniqueEvents);
        if (uniqueEvents.length > 0 && !selectedEvent) {
          setSelectedEvent(uniqueEvents[0].id);
        }
      }
    } catch (e) {
      console.error('Error fetching team events:', e);
      toast.error('Unable to fetch events. You may not have scanner access.');
    }
  };

  const fetchCheckInStats = async () => {
    if (!selectedEvent) return;
    try {
      const res = await axios.get(`${API_URL}/events/${selectedEvent}/check-ins`);
      setCheckInStats(res.data);
      setRecentScans(res.data.recent_scans || []);
    } catch (error) {
      console.error('Error fetching check-in stats:', error);
    }
  };

  const startScanner = async () => {
    setCameraError(null);
    setStartingCamera(true);
    
    try {
      // Clean up any existing scanner
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
        } catch (e) {
          // Ignore stop errors
        }
        html5QrCodeRef.current = null;
      }

      // Create new scanner instance
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      // Get available cameras
      const cameras = await Html5Qrcode.getCameras();
      
      if (!cameras || cameras.length === 0) {
        throw new Error('No cameras found. Please ensure camera access is enabled.');
      }

      // Prefer back camera on mobile
      let cameraId = cameras[0].id;
      const backCamera = cameras.find(cam => 
        cam.label.toLowerCase().includes('back') || 
        cam.label.toLowerCase().includes('rear') ||
        cam.label.toLowerCase().includes('environment')
      );
      if (backCamera) {
        cameraId = backCamera.id;
      }

      // Calculate QR box size based on viewport
      const viewportWidth = window.innerWidth;
      const qrboxSize = Math.min(250, viewportWidth - 80);

      // Start scanning with mobile-optimized config
      await html5QrCode.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: qrboxSize, height: qrboxSize },
          aspectRatio: 1.0,
          disableFlip: false
        },
        onScanSuccess,
        onScanFailure
      );

      setScanning(true);
      setStartingCamera(false);
      toast.success('Camera started! Point at QR code.');

    } catch (error) {
      console.error('Scanner error:', error);
      setStartingCamera(false);
      
      let errorMessage = 'Failed to start camera';
      
      if (error.message?.includes('Permission')) {
        errorMessage = 'Camera permission denied. Please allow camera access and try again.';
      } else if (error.message?.includes('NotFoundError') || error.message?.includes('No cameras')) {
        errorMessage = 'No camera found. Please ensure your device has a camera.';
      } else if (error.message?.includes('NotAllowedError')) {
        errorMessage = 'Camera access blocked. Please enable camera in your browser settings.';
      } else if (error.message?.includes('NotReadableError')) {
        errorMessage = 'Camera is in use by another app. Please close other camera apps.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setCameraError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (e) {
        console.error('Error stopping scanner:', e);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    // Pause scanning while processing
    await stopScanner();
    await processQRCode(decodedText);
  };

  const onScanFailure = (error) => {
    // Ignore scan failures (no QR detected)
  };

  const processQRCode = async (qrCode) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/tickets/scan`, { qr_code: qrCode });
      setLastScan(res.data);
      
      if (res.data.valid) {
        toast.success(`✓ ${res.data.ticket.fan_name} checked in!`);
        fetchCheckInStats();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      setLastScan({
        valid: false,
        error: 'NETWORK_ERROR',
        message: error.response?.data?.detail || 'Failed to scan ticket'
      });
      toast.error('Scan failed');
    } finally {
      setLoading(false);
    }
  };

  const handleManualScan = async () => {
    if (!manualCode.trim()) return;
    await processQRCode(manualCode.trim());
    setManualCode('');
  };

  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
              <QrCode className="w-6 h-6 text-primary" />
              Ticket Scanner
            </h1>
            <p className="text-muted-foreground">Scan QR codes to check in attendees</p>
          </div>
        </div>

        {/* Event Selector */}
        <Card className="bg-card border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-sm text-muted-foreground mb-1 block">Select Event</label>
                <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>{event.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="icon" onClick={fetchCheckInStats}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Check-in Stats */}
        {checkInStats && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="bg-card border-white/10">
              <CardContent className="p-4 text-center">
                <Ticket className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <div className="text-2xl font-bold">{checkInStats.total_tickets}</div>
                <div className="text-xs text-muted-foreground">Total Tickets</div>
              </CardContent>
            </Card>
            <Card className="bg-card border-white/10 border-l-4 border-l-green-500">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
                <div className="text-2xl font-bold text-green-500">{checkInStats.checked_in}</div>
                <div className="text-xs text-muted-foreground">Checked In</div>
              </CardContent>
            </Card>
            <Card className="bg-card border-white/10">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <div className="text-2xl font-bold">{checkInStats.remaining}</div>
                <div className="text-xs text-muted-foreground">Remaining</div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Scanner */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle>Scan QR Code</CardTitle>
              <CardDescription>Point your camera at the ticket QR code</CardDescription>
            </CardHeader>
            <CardContent>
              {!scanning && !startingCamera ? (
                <div className="text-center py-8">
                  <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <Camera className="w-12 h-12 text-primary" />
                  </div>
                  <Button onClick={startScanner} size="lg" className="gap-2" data-testid="start-scanner-btn">
                    <QrCode className="w-5 h-5" />
                    Start Scanner
                  </Button>
                  
                  {cameraError && (
                    <div className="mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      <CameraOff className="w-5 h-5 inline mr-2" />
                      {cameraError}
                    </div>
                  )}
                </div>
              ) : startingCamera ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                  <p className="text-muted-foreground">Starting camera...</p>
                  <p className="text-sm text-muted-foreground mt-2">Please allow camera access when prompted</p>
                </div>
              ) : (
                <div>
                  <div 
                    id="qr-reader" 
                    ref={scannerContainerRef} 
                    className="rounded-lg overflow-hidden mb-4"
                    style={{ minHeight: '300px' }}
                  ></div>
                  <Button variant="outline" onClick={stopScanner} className="w-full gap-2">
                    <CameraOff className="w-4 h-4" />
                    Stop Scanner
                  </Button>
                </div>
              )}

              {/* Manual Entry */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <label className="text-sm text-muted-foreground mb-2 block">Or enter code manually</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter QR code..."
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleManualScan()}
                    data-testid="manual-code-input"
                  />
                  <Button onClick={handleManualScan} disabled={!manualCode.trim() || loading}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scan Result & Recent */}
          <div className="space-y-6">
            {/* Last Scan Result */}
            {lastScan && (
              <Card className={`border-2 ${lastScan.valid ? 'border-green-500 bg-green-500/10' : 'border-red-500 bg-red-500/10'}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {lastScan.valid ? (
                      <CheckCircle className="w-12 h-12 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-12 h-12 text-red-500 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h3 className={`font-bold text-lg ${lastScan.valid ? 'text-green-500' : 'text-red-500'}`}>
                        {lastScan.valid ? 'Check-in Successful!' : 'Check-in Failed'}
                      </h3>
                      <p className="text-muted-foreground">{lastScan.message}</p>
                      
                      {lastScan.ticket && (
                        <div className="mt-4 p-3 bg-black/20 rounded-lg">
                          <p className="font-semibold">{lastScan.ticket.fan_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {lastScan.ticket.tier_name} • {lastScan.ticket.event_title}
                          </p>
                        </div>
                      )}
                      
                      {lastScan.previous_scan && (
                        <div className="mt-2 text-sm text-amber-400 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          Previously scanned by {lastScan.previous_scan.scanned_by}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full mt-4" 
                    onClick={async () => {
                      setLastScan(null);
                      await startScanner();
                    }}
                  >
                    Scan Next Ticket
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Recent Scans */}
            <Card className="bg-card border-white/10">
              <CardHeader>
                <CardTitle className="text-base">Recent Check-ins</CardTitle>
              </CardHeader>
              <CardContent>
                {recentScans.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No check-ins yet</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {recentScans.map((scan, i) => (
                      <div key={scan.id || i} className="flex items-center gap-3 p-2 bg-secondary/30 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{scan.scanned_by_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(scan.scanned_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
